import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';

import type { FeedbackTargetValues } from 'src/api/endpoints/storeApi.types';
import { useSubscriptionSuspense } from 'src/api/queries';
import { Feedback, Sheet } from 'src/components';
import { subscriptionHelpers } from 'src/utils/subscriptionHelpers';

import { useAuth0 } from '@auth0/auth0-react';
import { EnterpriseSubscriptionCard } from 'src/components/EnterpriseSubscriptionCard';
import { userHelpers } from 'src/utils/userHelpers';
import { SubscriptionCard } from '../components/SubscriptionCard';
import { SubscriptionFAQ } from '../components/SubscriptionFAQ';

export const SubscriptionPage = () => {
  const { t } = useLingui();
  const subscription = useSubscriptionSuspense();
  const auth = useAuth0();

  const isActiveAndManagementTypeSimple =
    subscriptionHelpers.isManagementTypeSimple(subscription?.data);

  // Old way to detect Enterprise Connect subscriptions.
  // We used to combine checking for org membership and free connect
  // to determine if the user was on an Enterprise Connect plan.
  const isEnterpriseConnectLegacy =
    subscriptionHelpers.isFreeConnect(subscription?.data) &&
    !!auth.user &&
    userHelpers.isOrganizationMember(auth.user);

  // New way to detect Enterprise Connect subscriptions.
  // The reason we can't simply just check for org membership is that
  // we have some corner cases where some enterprise org owners still
  // have the old multiple seats subscription and in those cases this
  // page should give them access to that until we can properly sunset.
  const isEnterpriseConnectNew =
    !!auth.user &&
    userHelpers.isOrganizationMember(auth.user) &&
    subscription.isSuccess &&
    !subscription.data;

  const isEnterpriseConnect =
    isEnterpriseConnectLegacy || isEnterpriseConnectNew;

  const feedbackTargetId = useMemo((): FeedbackTargetValues | null => {
    if (!subscription.data) return null;

    if (isEnterpriseConnect) {
      return 'connect-plan-page-v1';
    }

    if (isActiveAndManagementTypeSimple) {
      return 'connect-plan-page-v1';
    }

    // TODO: Check the member seat instead for the actual role name
    if (subscription.data.access.canSeeMembers) {
      return 'c4b-plan-page-for-owner-v1';
    }

    return 'c4b-plan-page-for-member-v1';
  }, [subscription.data]);

  return (
    <div className="flex flex-col gap-24 lm:gap-32" data-cy="subscription-page">
      {isEnterpriseConnect ? (
        <Sheet>
          <EnterpriseSubscriptionCard />
        </Sheet>
      ) : (
        <>
          <SubscriptionCard />
          <SubscriptionFAQ />
        </>
      )}
      {feedbackTargetId && (
        <Feedback
          feedbackTargetId={feedbackTargetId}
          title={t`Did you find what you were looking for?`}
        />
      )}
    </div>
  );
};
